import { useEffect, useRef } from "react";

import { useClose } from "@headlessui/react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { NotificationBadge } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationBadge";
import { useStore } from "@/store";
import { teamPageUrl } from "@/urls";
import { formatNotificationTime } from "@/utils/formatRelativeTime";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";
import { useIntervalValue } from "@/utils/useIntervalValue";

import type { Notification } from "@/api";
import type { NotificationBadgeVariant } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationBadge";
import type { EventMap } from "@/utils/trackEvent";

export const HOVER_READ_DELAY_MS = 300;

export function NotificationRow({
  notification,
  eventType,
  badge,
  children,
  href,
  onNavigate,
}: {
  notification: Notification;
  eventType: EventMap["notification_clicked"]["type"];
  badge?: NotificationBadgeVariant;
  children: React.ReactNode;
  href?: string;
  onNavigate?: () => boolean;
}) {
  const locale = useLocale();
  const t = useTranslations("sidebar.notifications");
  const tTime = useTranslations("time");
  const close = useClose();
  const { notifications } = useStore();
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markRead = () => notifications.markOneAsRead(notification.id);

  const cancelHover = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
  };

  const onPointerEnter = (e: React.PointerEvent) => {
    if (notification.isRead || e.pointerType !== "mouse") return;
    cancelHover();
    hoverTimer.current = setTimeout(markRead, HOVER_READ_DELAY_MS);
  };

  useEffect(() => cancelHover, []);

  const formattedTimestamp = useIntervalValue(
    () => formatNotificationTime(notification.createdAt, locale, tTime),
    30_000,
  );

  const onClick = (e: React.MouseEvent) => {
    trackEvent("notification_clicked", { type: eventType, is_read: notification.isRead });
    // Let the browser handle modified clicks (new tab etc.)
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    close();
    if (onNavigate?.()) e.preventDefault();
  };

  return (
    <div
      onPointerEnter={onPointerEnter}
      onPointerLeave={cancelHover}
      onClickCapture={markRead}
      onAuxClickCapture={(e) => {
        if (e.button === 1) markRead();
      }}
      className={twMerge(
        "relative flex rounded-10 p-2.5 hover:bg-gray-100 dark:hover:bg-gray-900",
        !notification.isRead && "bg-blue-50 dark:bg-blue-950",
      )}
    >
      {href && (
        // Clickable background; interactive children stack above it with `relative`
        <Link href={href} aria-label={t("open")} className="absolute inset-0" onClick={onClick} />
      )}
      <Link
        href={teamPageUrl({ teamPermalink: notification.sender.username })}
        className="relative shrink-0 self-start"
      >
        <Profile.Userpic
          name={notification.sender.name}
          src={notification.sender.avatarUrls?.[64]}
          size="sm"
          className="h-[34px] w-[34px] shrink-0 sm:h-[34px] sm:w-[34px]"
          badge={badge && <NotificationBadge variant={badge} />}
        />
      </Link>
      <div className="flex flex-1 min-w-0 flex-col gap-1 pl-2.5 pr-7">
        {children}
        <p
          className={twMerge(
            "font-geist text-[13px] leading-[21px] font-medium tracking-[-0.02em]",
            notification.isRead ? "text-gray-500" : "text-blue-500",
          )}
        >
          {formattedTimestamp}
        </p>
      </div>
      {!notification.isRead && (
        <span
          aria-hidden
          className="absolute right-5 top-[15px] h-2 w-2 rounded-full bg-blue-500"
        />
      )}
    </div>
  );
}
